import React from 'react';
import PropTypes from 'prop-types';

/**
 * Contractor claims the auditor has already decided, on the Contractor tab
 * beside the ones still waiting (the user, 2026-10-21):
 *   Approved   - finally approved by the auditor; nothing comes after.
 *   Sent back  - returned to the admin to fix. Once re-approved it is waiting
 *                on the auditor again and moves to the queue above.
 * Read-only: View opens the claim the same way the queue's View does.
 */

const TypeBadge = ({ claimType }) => (
  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
    claimType === 'ExpenseClaim' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
  }`}>
    {claimType === 'ExpenseClaim' ? 'Expense' : 'Per Diem'}
  </span>
);

TypeBadge.propTypes = { claimType: PropTypes.string };

const HistoryTable = ({ title, blurb, tone, rows, decidedLabel, kind, formatDate, formatCurrency, onView, limit }) => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-4 sm:mb-6">
    <div className={`px-4 sm:px-6 py-3 border-b border-gray-200 border-l-4 ${tone}`}>
      <h3 className="font-semibold text-gray-800">
        {title} <span className="text-sm font-normal text-gray-500">({rows.length})</span>
      </h3>
      <p className="text-xs text-gray-500 mt-0.5">{blurb}</p>
    </div>
    {rows.length === 0 ? (
      <p className="px-6 py-8 text-sm text-gray-500 text-center">None yet.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full responsive-table">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Claimant</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Region</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">{decidedLabel}</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">By</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                {kind === 'sentBack' ? 'Reason' : 'Notes'}
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={`${row.claimType}:${row.claimId}`} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-6 py-3 text-sm font-medium text-gray-900" data-cell="title">{row.userName || '-'}</td>
                <td className="px-6 py-3 text-sm text-gray-600" data-label="Region">{row.region || '-'}</td>
                <td className="px-6 py-3" data-label="Type"><TypeBadge claimType={row.claimType} /></td>
                <td className="px-6 py-3 text-sm font-semibold text-gray-800 text-right tabular-nums" data-label="Amount">
                  {formatCurrency(row.totalAmount)}
                </td>
                <td className="px-6 py-3 text-sm text-gray-600" data-label={decidedLabel}>{formatDate(row.decidedAt)}</td>
                <td className="px-6 py-3 text-sm text-gray-600" data-label="By">{row.decidedBy || '-'}</td>
                <td className="px-6 py-3 text-sm text-gray-700 max-w-xs break-words" data-label={kind === 'sentBack' ? 'Reason' : 'Notes'}>
                  {kind === 'sentBack'
                    ? (row.reason || '-')
                    : (row.timesSentBack > 0
                      ? `Sent back ${row.timesSentBack} time${row.timesSentBack === 1 ? '' : 's'} before approval`
                      : '-')}
                </td>
                <td className="px-6 py-3" data-cell="action">
                  <button
                    onClick={() => onView(row.claimId, row.claimType)}
                    className="px-2 py-1 text-sm md:text-xs border border-blue-300 text-blue-700 rounded hover:bg-blue-50 transition-colors"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
    {limit && rows.length >= limit && (
      <p className="px-6 py-2 text-xs text-gray-500 border-t border-gray-100">
        Showing the {limit} most recent.
      </p>
    )}
  </div>
);

HistoryTable.propTypes = {
  title: PropTypes.string.isRequired,
  blurb: PropTypes.string,
  tone: PropTypes.string,
  rows: PropTypes.array.isRequired,
  decidedLabel: PropTypes.string.isRequired,
  kind: PropTypes.oneOf(['approved', 'sentBack']).isRequired,
  formatDate: PropTypes.func.isRequired,
  formatCurrency: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  limit: PropTypes.number,
};

const ContractorClaimHistory = ({ history, loading, show = 'all', formatDate, formatCurrency, onView }) => {
  if (loading) {
    return <p className="text-sm text-gray-500 text-center py-6">Loading decided claims...</p>;
  }
  const common = { formatDate, formatCurrency, onView, limit: history?.limit };
  return (
    <>
      {(show === 'all' || show === 'approved') && (
        <HistoryTable
          {...common}
          kind="approved"
          title="Approved"
          blurb="Finally approved by the auditor. Nobody signs after this, so these claims are complete."
          tone="border-l-emerald-500"
          rows={history?.approved || []}
          decidedLabel="Approved on"
        />
      )}
      {(show === 'all' || show === 'sentBack') && (
        <HistoryTable
          {...common}
          kind="sentBack"
          title="Sent back"
          blurb="Returned to the admin to fix. Once they re-approve it, it is waiting on you again above."
          tone="border-l-amber-500"
          rows={history?.sentBack || []}
          decidedLabel="Sent back on"
        />
      )}
    </>
  );
};

ContractorClaimHistory.propTypes = {
  history: PropTypes.shape({
    approved: PropTypes.array,
    sentBack: PropTypes.array,
    limit: PropTypes.number,
  }),
  loading: PropTypes.bool,
  show: PropTypes.oneOf(['all', 'approved', 'sentBack']),
  formatDate: PropTypes.func.isRequired,
  formatCurrency: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
};

export default ContractorClaimHistory;
